import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@contexts/AuthContext';

// Tipos para o sistema de permissões
interface PermissionConfig {
  roles: Record<string, {
    name: string;
    permissions: string[];
  }>;
  permissions: Record<string, string>;
}

interface PermissionResult {
  hasPermission: boolean;
  reason?: string;
}

interface UsePermissionsReturn {
  checkPermission: (permission: string) => PermissionResult;
  hasRole: (role: string) => boolean;
  userRoles: string[];
  isLoading: boolean;
  error: string | null;
}

// Cache para configuração de permissões
let permissionConfig: PermissionConfig | null = null;
let configLoadPromise: Promise<PermissionConfig> | null = null;

/**
 * Hook para gerenciar permissões conforme ERPApi/docs/seguranca/autorizacao.md
 */
export const usePermissions = (): UsePermissionsReturn => {
  const { user } = useAuth();
  // Start as true when config not yet cached, so PermissionGuard shows skeleton
  const [isLoading, setIsLoading] = useState(!permissionConfig);
  const [error, setError] = useState<string | null>(null);

  // Carregar configuração de permissões
  const loadPermissionConfig = useCallback(async (): Promise<PermissionConfig> => {
    if (permissionConfig) {
      return permissionConfig;
    }

    if (configLoadPromise) {
      return configLoadPromise;
    }

    configLoadPromise = (async () => {
      try {
        const response = await fetch('/security/permission-config.json');
        
        if (!response.ok) {
          throw new Error(`Falha ao carregar configuração de permissões: ${response.status} ${response.statusText}`);
        }

        const config: PermissionConfig = await response.json();
        permissionConfig = config;
        return config;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
        console.error('Erro ao carregar configuração de permissões:', errorMessage);
        throw err;
      } finally {
        configLoadPromise = null;
      }
    })();

    return configLoadPromise;
  }, []);

  // Load permission configuration on initialization.
  // Every instance that renders before the config is ready must set its own
  // isLoading state so PermissionGuard shows a skeleton instead of a false denial.
  useEffect(() => {
    if (permissionConfig) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    loadPermissionConfig()
      .then(() => setIsLoading(false))
      .catch((err) => {
        console.error('Error loading permissions:', err);
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
        setIsLoading(false);
      });
  }, [loadPermissionConfig]);

  // Extract user roles with improved logic
  let userRoles: string[] = [];
  
  // First, try to use user.roles if it exists
  if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
    userRoles = user.roles
      .filter((role): role is string => {
        const isValid = role !== null && role !== undefined && typeof role === 'string' && role.trim().length > 0;
        return isValid;
      })
      .map(role => role.trim());
  }
  
  // If user.roles didn't work, use user.role as fallback
  if (userRoles.length === 0 && user?.role && typeof user.role === 'string' && user.role.trim().length > 0) {
    userRoles = [user.role.trim()];
  }

  /**
   * Verificar se usuário tem uma permissão específica
   */
  const checkPermission = useCallback((permission: string): PermissionResult => {
    if (!user) {
      return { 
        hasPermission: false, 
        reason: 'Usuário não autenticado' 
      };
    }

    // PRIMEIRO: Tentar usar permissões diretas do usuário (authService) como atalho positivo
    if (user.permissions && Array.isArray(user.permissions) && user.permissions.length > 0) {
      // Admin tem acesso total
      if (user.permissions.includes('*')) {
        return { hasPermission: true };
      }
      
      // Permissão encontrada diretamente → retorna imediatamente (fast path)
      if (user.permissions.includes(permission)) {
        return { hasPermission: true };
      }
      // Não encontrada nas permissões diretas → continua verificando pelas roles no config
    }

    // SECUNDÁRIO: Usar configuração de permissões via roles
    if (permissionConfig) {
      for (const role of userRoles) {
        const roleConfig = permissionConfig.roles[role];
        if (!roleConfig) continue;

        if (roleConfig.permissions.includes('*')) {
          return { hasPermission: true };
        }

        if (roleConfig.permissions.includes(permission)) {
          return { hasPermission: true };
        }
      }
    } else {
      // Fallback: If configuration didn't load but user is admin, allow access
      const userRole = user.role || user.roles?.[0];
      if (userRole === 'admin' || userRole === 'administrator') {
        return { hasPermission: true };
      }
    }

    return { 
      hasPermission: false, 
      reason: userRoles.length > 0 
        ? `Role(s) [${userRoles.join(', ')}] do not have permission '${permission}'`
        : 'User has no valid roles'
    };
  }, [user, userRoles, permissionConfig]);

  /**
   * Verificar se usuário tem uma role específica
   */
  const hasRole = useCallback((role: string): boolean => {
    return userRoles.includes(role);
  }, [userRoles]);

  return {
    checkPermission,
    hasRole,
    userRoles,
    isLoading,
    error
  };
};

/**
 * Hook para verificar múltiplas permissões
 */
export const useMultiplePermissions = (permissions: string[]) => {
  const { checkPermission } = usePermissions();

  return permissions.map(permission => ({
    permission,
    result: checkPermission(permission)
  }));
};

/**
 * Hook para verificar se usuário pode acessar alguma das permissões (OR)
 */
export const useAnyPermission = (permissions: string[]) => {
  const { checkPermission } = usePermissions();

  const hasAnyPermission = permissions.some(permission => 
    checkPermission(permission).hasPermission
  );

  return { hasAnyPermission };
};

/**
 * Hook para verificar se usuário tem todas as permissões (AND)
 */
export const useAllPermissions = (permissions: string[]) => {
  const { checkPermission } = usePermissions();

  const hasAllPermissions = permissions.every(permission => 
    checkPermission(permission).hasPermission
  );

  return { hasAllPermissions };
};